import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {yyyyMMdd} from '../../typeAliases';
import {MasterService} from '../../../core/service/master.service';
import {CommonService} from '../../../core/service/common.service';
import {IKUSEI, IKUSUU, SHIYOULOT_KEY_SUFFIX, SHUKKA, SHUKKA_EVENT_NAME} from '../../constants';
import {SearchKeyForShiyoulot2} from '../../interfaces/search-key-for-shiyoulot';
import {Observable, Subscription} from 'rxjs';
import {ShiyoulotV2} from '../../interfaces/shiyoulot';
import {ShiyoulotService} from '../../../core/service/shiyoulot.service';
import {EventService} from '../../../core/service/event.service';
import {ShukkaEvent} from '../../interfaces/shukka-event';
import {TemplateRefService} from '../../../core/service/template-ref.service';

export interface SelectShiyoulotDialogData {
  keishuId: string,
  selectedShukkaEventIds: (string | null | undefined)[],
  keishaShubetsu?: typeof IKUSUU | typeof IKUSEI,
  edukeDate?: yyyyMMdd,
  nyuukaHimodukiInfoList: NyuukaHimodukiInfo[], // 飼養ロットの紐付きがある全入荷イベントの情報。帳票からの場合空配列。
}

/**
 * 入荷イベントidと紐づいている出荷イベントid
 */
export interface NyuukaHimodukiInfo {
  nyuukaEventId: string; // 入荷イベント-id(ex. 育成ロットの入荷イベントid)
  himodukiId: string; // 入荷イベント-紐付きid(ex. 育成ロットの入荷イベントの紐付きid(=育雛ロットの出荷イベントid))
}

export interface SelectShiyoulotDialogResp {
  shiyoulotId: string,
  shukkaEvent: ShukkaEvent
}

@Component({
  selector: 'app-select-shiyoulot-dialog',
  templateUrl: './select-shiyoulot-dialog.component.html',
  styleUrls: ['./../../layout/layout.component.scss', './select-shiyoulot-dialog.component.scss']
})
export class SelectShiyoulotDialogComponent implements OnInit, OnDestroy {
  selectedKeishaId?: string;
  shiyoulotListObservable: Observable<ShiyoulotV2[]> = new Observable<ShiyoulotV2[]>();
  subscription: Subscription = new Subscription();
  shukkaEventMap = new Map<string, ShukkaEvent[]>(); // key: 飼養ロットID, value: ShukkaEvent[]

  constructor(@Inject(MAT_DIALOG_DATA) public selectShiyoulotDialogData: SelectShiyoulotDialogData,
              public matDialogRef: MatDialogRef<SelectShiyoulotDialogComponent>,
              public masterService: MasterService,
              public commonService: CommonService,
              public shiyoulotService: ShiyoulotService,
              public eventService: EventService,
              public templateRefService: TemplateRefService
  ) { }

  /**
   *
   */
  ngOnInit(): void {
  }

  /**
   *
   */
  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /**
   * 飼養ロット検索
   */
  searchShiyoulot() {
    const kensakuKey: SearchKeyForShiyoulot2 = {
      edukeDate: this.selectShiyoulotDialogData.edukeDate,
      keishuId: this.selectShiyoulotDialogData.keishuId,
      keishaId: this.selectedKeishaId
    };
    this.shiyoulotListObservable = this.shiyoulotService.getShiyoulotObservable2(kensakuKey);
    if (this.shiyoulotListObservable) {
      // 新しくデータを取得する前にそれ以前にsubsribeしていたsubscriptionを初期化。
      this.subscription = this.commonService.subScriptionInit(this.subscription);
      this.subscription.add(
        this.shiyoulotListObservable.subscribe(async(shiyoulotList) => {
          console.log(shiyoulotList);
          for (const shiyoulot of shiyoulotList) {
            if (shiyoulot.id) {
              const shukkaEvents = await this.eventService.getEvent({
                eventTaishouIds: [shiyoulot.id + SHIYOULOT_KEY_SUFFIX],
                eventShubetsu: SHUKKA,
                willJisshi: true
              });
              // オールアウトの出荷イベントは除外
              const shukkaEventsExceptAllout = shukkaEvents.filter((ev: ShukkaEvent) => ev.eventName !== SHUKKA_EVENT_NAME.ALL_OUT);
              this.shukkaEventMap.set(shiyoulot.id, shukkaEventsExceptAllout);
            }
          }
        })
      );
    }
  }

  /**
   * 飼養ロットIDと選択した出荷イベントを返す
   * @param shiyoulotId
   * @param shukkaEvent
   * @param selectShiyoulotDialogData
   */
  selectShukkaEvent(shiyoulotId: string, shukkaEvent: ShukkaEvent, selectShiyoulotDialogData: SelectShiyoulotDialogData) {
    if (this.isHimodukiDone(shukkaEvent, selectShiyoulotDialogData)) {
      return;
    }
    const selectShiyoulotModalResp: SelectShiyoulotDialogResp = { shiyoulotId, shukkaEvent };
    this.commonService.onClick(this.matDialogRef, selectShiyoulotModalResp);
  }

  /**
   * 出荷イベントが紐づいているかチェック
   * 出荷イベントと紐づいている入荷イベントが別の飼養ロットと紐づいている場合、
   * または登録済、未登録含め対象の飼養ロットの入荷イベントと紐づいている場合(飼養ロット選択モーダルにて選択済)true
   * @param shukkaEvent
   * @param selectShiyoulotDialogData
   */
  isHimodukiDone(shukkaEvent: ShukkaEvent, selectShiyoulotDialogData: SelectShiyoulotDialogData) {
    // 出荷イベントと紐づいている入荷イベント
    const originalHimodukiNyuukaEvent = selectShiyoulotDialogData.nyuukaHimodukiInfoList.find(ev => ev.nyuukaEventId === shukkaEvent.himodukiId);
    // 出荷イベントと紐づいている入荷イベントが別の飼養ロットと紐づいている場合、
    // 対象の飼養ロットで既に他の入荷イベントに紐づいている場合は紐づいていると判定
    if ((shukkaEvent.himodukiId && !originalHimodukiNyuukaEvent)
      || selectShiyoulotDialogData.selectedShukkaEventIds.includes(shukkaEvent.id)) {
      return true;
    }
    return false;
  }
}
